import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Edit3, Trash2, Save, Loader2, Check, Languages, Globe,
  Image, User, CreditCard, FileCheck, FileBadge, BookOpen, FileImage, Files,
  IdCard, Camera, Building2, FileSearch, FileClock, FileWarning, FileHeart, FileKey
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// أيقونات وألوان متنوعة للمستندات
const DOCUMENT_ICONS = [
  { icon: Image, color: '#10B981', bg: 'bg-emerald-500/20' },
  { icon: BookOpen, color: '#3B82F6', bg: 'bg-blue-500/20' },
  { icon: IdCard, color: '#8B5CF6', bg: 'bg-purple-500/20' },
  { icon: FileBadge, color: '#F59E0B', bg: 'bg-amber-500/20' },
  { icon: FileCheck, color: '#EC4899', bg: 'bg-pink-500/20' },
  { icon: FileSearch, color: '#06B6D4', bg: 'bg-cyan-500/20' },
  { icon: Building2, color: '#EF4444', bg: 'bg-red-500/20' },
  { icon: CreditCard, color: '#14B8A6', bg: 'bg-teal-500/20' },
  { icon: Camera, color: '#F97316', bg: 'bg-orange-500/20' },
  { icon: FileText, color: '#6366F1', bg: 'bg-indigo-500/20' },
  { icon: FileClock, color: '#84CC16', bg: 'bg-lime-500/20' },
  { icon: FileWarning, color: '#EAB308', bg: 'bg-yellow-500/20' },
  { icon: FileHeart, color: '#FB7185', bg: 'bg-rose-500/20' },
  { icon: FileKey, color: '#A855F7', bg: 'bg-fuchsia-500/20' },
  { icon: User, color: '#22C55E', bg: 'bg-green-500/20' },
  { icon: FileText, color: '#c9a227', bg: 'bg-[#c9a227]/20' },
];

const getDocumentStyle = (index) => {
  return DOCUMENT_ICONS[index % DOCUMENT_ICONS.length];
};

// File-type metadata (PDF / Image / Both)
const FILE_TYPE_META = {
  pdf:   { ar: 'PDF فقط',         en: 'PDF only',        icon: FileText,  cls: 'bg-red-500/10 border-red-500/30 text-red-300' },
  image: { ar: 'صورة فقط',        en: 'Image only',      icon: FileImage, cls: 'bg-blue-500/10 border-blue-500/30 text-blue-300' },
  both:  { ar: 'PDF أو صورة',     en: 'PDF or Image',    icon: Files,     cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
  // legacy aliases
  file:  { ar: 'PDF أو صورة',     en: 'PDF or Image',    icon: Files,     cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
};

export default function MasterDocuments() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [translatingName, setTranslatingName] = useState(false);
  const [translatingDesc, setTranslatingDesc] = useState(false);
  
  // حالة خيار الهوية من دولة المقيم
  const [residentIdOption, setResidentIdOption] = useState({
    enabled: false,
    selected_nationalities: []
  });
  const [loadingResidentId, setLoadingResidentId] = useState(false);
  const [nationalities, setNationalities] = useState([]);  // catalogue of all countries
  const [nationalitySearch, setNationalitySearch] = useState('');
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    document_type: 'both',  // 'pdf' | 'image' | 'both'
  });

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // ترجمة النص
  const translateText = async (text, fromLang, toLang) => {
    try {
      const response = await axios.post(`${API_URL}/api/translate`, {
        text,
        from_lang: fromLang,
        to_lang: toLang
      }, { headers: getHeaders() });
      return response.data.translated || '';
    } catch (error) {
      console.error('Translation error:', error);
      return '';
    }
  };

  const handleTranslateName = async () => {
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'أدخل الاسم بالعربية أولاً' : 'Enter Arabic name first');
      return;
    }
    setTranslatingName(true);
    try {
      const translated = await translateText(formData.name_ar, 'ar', 'en');
      if (translated) {
        setFormData({ ...formData, name_en: translated });
      }
    } finally {
      setTranslatingName(false);
    }
  };

  const handleTranslateDesc = async () => {
    if (!formData.description_ar.trim()) {
      toast.error(language === 'ar' ? 'أدخل الوصف بالعربية أولاً' : 'Enter Arabic description first');
      return;
    }
    setTranslatingDesc(true);
    try {
      const translated = await translateText(formData.description_ar, 'ar', 'en');
      if (translated) {
        setFormData({ ...formData, description_en: translated });
      }
    } finally {
      setTranslatingDesc(false);
    }
  };

  // جلب المستندات
  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-documents`, {
        headers: getHeaders()
      });
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  // جلب إعدادات الهوية من دولة المقيم
  const fetchResidentIdOption = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-documents/resident-id-option`, {
        headers: getHeaders()
      });
      if (response.data.success) {
        setResidentIdOption(response.data.data || { enabled: false, selected_nationalities: [] });
      }
    } catch (error) {
      console.error('Error fetching resident ID option:', error);
    }
  };

  // جلب قائمة الجنسيات (Master catalogue)
  const fetchNationalities = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/master-documents/nationalities`, {
        headers: getHeaders()
      });
      if (res.data?.success) {
        setNationalities(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching nationalities:', err);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchResidentIdOption();
    fetchNationalities();
  }, [token]);

  // تفعيل/تعطيل خيار الهوية من دولة المقيم
  const toggleResidentIdOption = async () => {
    setLoadingResidentId(true);
    try {
      const newEnabled = !residentIdOption.enabled;
      const response = await axios.put(`${API_URL}/api/master-documents/resident-id-option`, {
        enabled: newEnabled,
        selected_nationalities: residentIdOption.selected_nationalities
      }, { headers: getHeaders() });
      
      if (response.data.success) {
        setResidentIdOption({ ...residentIdOption, enabled: newEnabled });
        toast.success(newEnabled 
          ? (language === 'ar' ? 'تم تفعيل خيار الهوية' : 'Resident ID option enabled')
          : (language === 'ar' ? 'تم تعطيل خيار الهوية' : 'Resident ID option disabled')
        );
      }
    } catch (error) {
      console.error('Error toggling resident ID option:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setLoadingResidentId(false);
    }
  };

  const openAddModal = () => {
    setEditingDoc(null);
    setFormData({ name_ar: '', name_en: '', description_ar: '', description_en: '', document_type: 'both' });
    setShowModal(true);
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setFormData({
      name_ar: doc.name_ar || '',
      name_en: doc.name_en || '',
      description_ar: doc.description_ar || '',
      description_en: doc.description_en || '',
      document_type: ['pdf', 'image', 'both'].includes(doc.document_type) ? doc.document_type : 'both',
    });
    setShowModal(true);
  };

  // Toggle a nationality on/off (persists to backend on every change)
  const toggleNationality = async (code) => {
    const current = new Set(residentIdOption.selected_nationalities || []);
    if (current.has(code)) current.delete(code); else current.add(code);
    const next = Array.from(current);
    // Optimistic UI
    setResidentIdOption({ ...residentIdOption, selected_nationalities: next });
    try {
      await axios.put(`${API_URL}/api/master-documents/resident-id-option`, {
        enabled: residentIdOption.enabled,
        selected_nationalities: next,
      }, { headers: getHeaders() });
    } catch (err) {
      // Revert on failure
      toast.error(language === 'ar' ? 'فشل حفظ الجنسيات' : 'Failed to save nationalities');
      setResidentIdOption({ ...residentIdOption });
    }
  };

  const handleSave = async () => {
    if (!formData.name_ar.trim()) {
      toast.error(language === 'ar' ? 'الاسم بالعربية مطلوب' : 'Arabic name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingDoc) {
        await axios.put(`${API_URL}/api/master-documents/${editingDoc.id}`, formData, {
          headers: getHeaders()
        });
        toast.success(language === 'ar' ? 'تم التحديث بنجاح' : 'Updated successfully');
      } else {
        await axios.post(`${API_URL}/api/master-documents`, formData, {
          headers: getHeaders()
        });
        toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Added successfully');
      }
      setShowModal(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/master-documents/${id}`, {
        headers: getHeaders()
      });
      toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setDeleting(null);
    }
  };

  const toggleActive = async (doc) => {
    try {
      await axios.put(`${API_URL}/api/master-documents/${doc.id}`, {
        ...doc,
        is_active: !doc.is_active
      }, { headers: getHeaders() });
      fetchDocuments();
      toast.success(doc.is_active 
        ? (language === 'ar' ? 'تم التعطيل' : 'Deactivated')
        : (language === 'ar' ? 'تم التفعيل' : 'Activated')
      );
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const seedDefaults = async () => {
    // Legacy — auto-seed now runs silently on backend GET. Kept as no-op to
    // avoid breaking anything that might still import it.
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <DashboardLayout
      title="قائمة الأوراق المطلوبة"
      titleEn="Required Documents List"
      subtitle="إدارة قائمة الأوراق المركزية للنظام بالكامل"
      subtitleEn="Central documents list managed system-wide"
      screenCode="ASB-005"
      backPath="/control-panel-settings"
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platforms Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'قائمة الأوراق المطلوبة', labelEn: 'Required Documents List', path: null }
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 flex items-center justify-center">
              <FileText className="h-6 w-6 text-[#d4af37]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? 'الأوراق المطلوبة' : 'Required Documents'}
              </h2>
              <p className="text-sm text-[#71717A]">
                {documents.length + 1} {language === 'ar' ? 'عنصر' : 'items'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={openAddModal}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid="add-document-btn"
            >
              <Plus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'إضافة ورقة جديدة' : 'Add New Document'}
            </Button>
          </div>
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
          </div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden"
          >
            {/* Table Header */}
            <div className="p-4 border-b border-white/10 grid grid-cols-12 gap-4 text-sm text-[#71717A]">
              <div className="col-span-1">#</div>
              <div className="col-span-4">{language === 'ar' ? 'اسم الورقة' : 'Document Name'}</div>
              <div className="col-span-4">{language === 'ar' ? 'الوصف' : 'Description'}</div>
              <div className="col-span-3 text-center">{language === 'ar' ? 'الإجراءات' : 'Actions'}</div>
            </div>
            
            <div className="divide-y divide-white/5">
              {/* خيار الهوية الصادرة من دولة المقيم - كصف أول في الجدول */}
              <motion.div
                variants={itemVariants}
                className={`p-4 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.02] transition-colors ${!residentIdOption.enabled ? 'opacity-70' : ''}`}
                data-testid="resident-id-row"
              >
                <div className="col-span-1 flex items-center gap-2">
                  <span className="text-[#71717A] text-sm">-</span>
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <Globe className="h-4 w-4 text-violet-400" />
                  </div>
                </div>
                
                <div className="col-span-4">
                  <h3 className="text-white font-medium">
                    {language === 'ar' ? 'الهوية الصادرة من دولة المقيم' : 'Resident Country ID'}
                  </h3>
                  {residentIdOption.enabled && (
                    <p className="text-[10px] text-violet-300 mt-0.5">
                      {language === 'ar' ? '✓ مفعّلة — اختر الجنسيات داخل كل خدمة' : '✓ Enabled — pick nationalities inside each service'}
                    </p>
                  )}
                </div>
                
                <div className="col-span-4">
                  <p className="text-[#71717A] text-sm">
                    {language === 'ar' 
                      ? 'فعّل هذا الخيار ليظهر بند "الجنسية" داخل كل خدمة. اختيار الجنسيات نفسها يتم من إعدادات الخدمة، لا من هنا.' 
                      : 'Enable this to expose a nationality picker inside every service. The actual nationality selection happens per-service, not here.'}
                  </p>
                </div>
                
                <div className="col-span-3 flex items-center justify-center gap-2">
                  <Switch
                    checked={residentIdOption.enabled}
                    onCheckedChange={toggleResidentIdOption}
                    disabled={loadingResidentId}
                    data-testid="resident-id-switch"
                    className="data-[state=checked]:bg-violet-500"
                  />
                  
                  <button
                    disabled
                    className="p-2 rounded-lg bg-white/[0.02] border border-white/10 opacity-30 cursor-not-allowed"
                    title={language === 'ar' ? 'غير قابل للتعديل' : 'Not editable'}
                  >
                    <Edit3 className="h-4 w-4 text-[#71717A]" />
                  </button>
                  
                  <button
                    disabled
                    className="p-2 rounded-lg bg-white/[0.02] border border-white/10 opacity-30 cursor-not-allowed"
                    title={language === 'ar' ? 'غير قابل للحذف' : 'Not deletable'}
                  >
                    <Trash2 className="h-4 w-4 text-[#71717A]" />
                  </button>
                </div>
              </motion.div>

              {/* NOTE (Feb 2026): The nationality picker used to live here.
                  Product owner asked to remove it — this row is now a simple
                  toggle. Nationalities are picked per-service inside the
                  service management page (ServiceRequiredDocs). */}

              {/* باقي الأوراق المطلوبة */}
              {documents.map((doc, index) => {
                const docStyle = getDocumentStyle(index);
                const IconComponent = docStyle.icon;
                const fileTypeMeta = FILE_TYPE_META[doc.document_type] || FILE_TYPE_META.both;
                const FileTypeIcon = fileTypeMeta.icon;
                
                return (
                  <motion.div
                    key={doc.id}
                    variants={itemVariants}
                    className={`p-4 grid grid-cols-12 gap-4 items-center hover:bg-white/[0.02] transition-colors ${!doc.is_active ? 'opacity-50' : ''}`}
                    data-testid={`doc-${doc.id}`}
                  >
                    <div className="col-span-1 flex items-center gap-2">
                      <span className="text-[#71717A] text-sm">{index + 1}</span>
                      <div className={`w-8 h-8 rounded-lg ${docStyle.bg} flex items-center justify-center`}>
                        <IconComponent className="h-4 w-4" style={{ color: docStyle.color }} />
                      </div>
                    </div>
                    
                    <div className="col-span-4">
                      <h3 className="text-white font-medium">
                        {language === 'ar' ? doc.name_ar : doc.name_en}
                      </h3>
                      {/* File type badge */}
                      <span
                        className={`inline-flex items-center gap-1 mt-1 text-[10px] px-2 py-0.5 rounded-full border ${fileTypeMeta.cls}`}
                        data-testid={`doc-filetype-${doc.id}`}
                      >
                        <FileTypeIcon className="h-3 w-3" />
                        {language === 'ar' ? fileTypeMeta.ar : fileTypeMeta.en}
                      </span>
                    </div>
                    
                    <div className="col-span-4">
                      <p className="text-[#71717A] text-sm line-clamp-2">
                        {(language === 'ar' ? doc.description_ar : doc.description_en) || '-'}
                      </p>
                    </div>
                    
                    <div className="col-span-3 flex items-center justify-center gap-2">
                      <Switch
                        checked={!!doc.is_active}
                        onCheckedChange={() => toggleActive(doc)}
                        data-testid={`doc-switch-${doc.id}`}
                        className="data-[state=checked]:bg-emerald-500"
                      />
                      
                      <button
                        onClick={() => openEditModal(doc)}
                        className="p-2 rounded-lg bg-white/[0.02] border border-white/10 hover:bg-blue-500/10 hover:border-blue-500/30 transition-colors"
                        title={language === 'ar' ? 'تعديل' : 'Edit'}
                        data-testid={`doc-edit-${doc.id}`}
                      >
                        <Edit3 className="h-4 w-4 text-blue-400" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleting === doc.id}
                        className="p-2 rounded-lg bg-white/[0.02] border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 transition-colors"
                        title={language === 'ar' ? 'حذف' : 'Delete'}
                        data-testid={`doc-delete-${doc.id}`}
                      >
                        {deleting === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-red-400" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-400" />
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}

              {documents.length === 0 && (
                <div className="p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-[#d4af37] opacity-30" />
                  <p className="text-[#A1A1AA]">
                    {language === 'ar' ? 'لا توجد أوراق أخرى' : 'No other documents'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#d4af37]" />
                {editingDoc 
                  ? (language === 'ar' ? 'تعديل الورقة' : 'Edit Document')
                  : (language === 'ar' ? 'إضافة ورقة جديدة' : 'Add New Document')
                }
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-[#71717A] mb-2">
                  {language === 'ar' ? 'الاسم بالعربية *' : 'Arabic Name *'}
                </label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  placeholder={language === 'ar' ? 'مثال: صورة شخصية' : 'e.g., Personal Photo'}
                  className="bg-white/[0.02] border-white/10 text-white rounded-xl"
                  dir="rtl"
                  data-testid="name-ar-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-[#71717A]">
                    {language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'}
                  </label>
                  <button
                    type="button"
                    onClick={handleTranslateName}
                    disabled={translatingName || !formData.name_ar.trim()}
                    className="flex items-center gap-1 text-xs text-[#d4af37] hover:text-[#f5d742] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {translatingName ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Languages className="h-3 w-3" />
                    )}
                    {language === 'ar' ? 'ترجمة' : 'Translate'}
                  </button>
                </div>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  placeholder="e.g., Personal Photo"
                  className="bg-white/[0.02] border-white/10 text-white rounded-xl"
                  dir="ltr"
                  data-testid="name-en-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#71717A] mb-2">
                  {language === 'ar' ? 'الوصف بالعربية (اختياري)' : 'Arabic Description (optional)'}
                </label>
                <Input
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                  placeholder={language === 'ar' ? 'وصف مختصر...' : 'Short description...'}
                  className="bg-white/[0.02] border-white/10 text-white rounded-xl"
                  dir="rtl"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-[#71717A]">
                    {language === 'ar' ? 'الوصف بالإنجليزية (اختياري)' : 'English Description (optional)'}
                  </label>
                  <button
                    type="button"
                    onClick={handleTranslateDesc}
                    disabled={translatingDesc || !formData.description_ar.trim()}
                    className="flex items-center gap-1 text-xs text-[#d4af37] hover:text-[#f5d742] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {translatingDesc ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Languages className="h-3 w-3" />
                    )}
                    {language === 'ar' ? 'ترجمة' : 'Translate'}
                  </button>
                </div>
                <Input
                  value={formData.description_en}
                  onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                  placeholder="Short description..."
                  className="bg-white/[0.02] border-white/10 text-white rounded-xl"
                  dir="ltr"
                />
              </div>

              {/* نوع الملف المقبول — PDF / صورة / كلاهما */}
              <div>
                <label className="block text-sm text-[#71717A] mb-2">
                  {language === 'ar' ? 'نوع الملف المقبول' : 'Accepted file type'}
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="filetype-picker">
                  {[
                    { key: 'pdf',   ar: 'PDF فقط',     en: 'PDF only',    icon: FileText },
                    { key: 'image', ar: 'صورة فقط',    en: 'Image only',  icon: FileImage },
                    { key: 'both',  ar: 'كلاهما',      en: 'Both',        icon: Files },
                  ].map(({ key, ar, en, icon: Icon }) => {
                    const active = formData.document_type === key || (key === 'both' && formData.document_type === 'file');
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, document_type: key })}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border transition-all ${
                          active
                            ? 'bg-[#d4af37]/15 border-[#d4af37]/50 text-[#f5d742] shadow-[0_0_0_1px_rgba(212,175,55,0.3)]'
                            : 'bg-white/[0.02] border-white/10 text-[#A1A1AA] hover:border-[#d4af37]/30'
                        }`}
                        data-testid={`filetype-${key}-btn`}
                      >
                        <Icon className="h-5 w-5" />
                        <span className="text-xs font-medium">
                          {language === 'ar' ? ar : en}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#71717A] mt-1.5">
                  {language === 'ar'
                    ? 'سيظهر هذا النوع للعميل عند رفع المستند، وينعكس تلقائياً على جميع الخدمات.'
                    : 'This is enforced when the customer uploads the file, and auto-reflects in every linked service.'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-white/10 text-white hover:bg-white/[0.05]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
                data-testid="save-document-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin me-2" />
                ) : (
                  <Save className="h-4 w-4 me-2" />
                )}
                {language === 'ar' ? 'حفظ' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </DashboardLayout>
  );
}
